import * as React from 'react'
import {
  ArrowUpRight,
  Check,
  Copy,
  ExternalLink,
  GitBranch,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Trash2
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
  PROXY_API_V1_URL,
  useListIngestMappingConfigsQuery,
  usePatchIngestIntakeMutation,
  useSwitchBranch,
  useUnarchiveIngestMappingConfigMutation,
  type IngestIntake,
  type IngestIntakeTabularTarget
} from '@axilon-apps/domains-ontology-ui-state'

import { writeClipboardPayload } from '../../../components/Chat/copyRenderedMarkdown'
import { StatusPill } from '../../../components/primitives/StatusPill'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu'
import { Switch } from '../../../components/ui/switch'
import { formatRelativeTime } from '../../../lib/time'
import { cn } from '../../../lib/utils'
import {
  API_KEY_TOKEN_PLACEHOLDER,
  buildIntakeUploadCurl,
  intakePushFilesUrl
} from '../../SettingsPage/components/api-keys/copy-curl'
import { UnarchiveConfigDialog } from '../../TabularConfigPage/dialogs/UnarchiveConfigDialog'
import { tabularConfigPath } from '../../TabularIngestJobPage/route'
import { extractErrorMessage } from '../util/extractErrorMessage'
import type { LandingSite } from '../wizard/IngestLanding'
import { IntakeLedger } from './IntakeLedger'
import {
  attentionPills,
  intakeMergeReviewPath,
  intakeUnarchiveVersionIds,
  intakesTabPath,
  siteUuid,
  targetCell,
  type IntakeTargetCell
} from './intake-view'

const COLUMN_COUNT = 7
const EXPAND_MS = 200

interface IntakesTableProps {
  intakes: IngestIntake[]
  sites: LandingSite[]
  expandedId: string | null
  onExpandedChange: (id: string | null) => void
  onEdit: (intake: IngestIntake) => void
  onDelete: (intake: IngestIntake) => void
}

export function IntakesTable({
  intakes,
  sites,
  expandedId,
  onExpandedChange,
  onEdit,
  onDelete
}: IntakesTableProps) {
  const [patchIntake] = usePatchIngestIntakeMutation()
  const [unarchiveConfig] = useUnarchiveIngestMappingConfigMutation()
  const { switchBranch, isOpening } = useSwitchBranch()
  const configsQuery = useListIngestMappingConfigsQuery({
    kind: 'mapped_tabular_ingest',
    include_archived: true,
    limit: 500
  })
  const configs = configsQuery.data
  const [enabledOverrides, setEnabledOverrides] = React.useState<Record<string, boolean>>({})
  const [copiedId, setCopiedId] = React.useState<string | null>(null)
  const [restoring, setRestoring] = React.useState<IngestIntake | null>(null)
  const [restoreOpen, setRestoreOpen] = React.useState(false)
  const [restoreBusy, setRestoreBusy] = React.useState(false)
  const [restoreError, setRestoreError] = React.useState<string | null>(null)
  const siteNames = React.useMemo(() => {
    const pairs: Array<[string, string]> = []
    for (const site of sites) {
      pairs.push([site.id, site.name])
      const uuid = siteUuid(site.id)
      if (uuid) pairs.push([uuid, site.name])
    }
    return Object.fromEntries(pairs)
  }, [sites])

  const openRestore = (intake: IngestIntake) => {
    setRestoreError(null)
    setRestoring(intake)
    setRestoreOpen(true)
  }

  const confirmRestore = async () => {
    if (!restoring) return
    const ids = intakeUnarchiveVersionIds(restoring, configs ?? [])
    if (!ids.length) return
    setRestoreBusy(true)
    setRestoreError(null)
    try {
      for (const id of ids) await unarchiveConfig(id).unwrap()
      const enabled = enabledOverrides[restoring.id] ?? restoring.enabled
      if (!enabled) {
        setEnabledOverrides((current) => ({ ...current, [restoring.id]: true }))
        try {
          await patchIntake({ id: restoring.id, body: { enabled: true } }).unwrap()
        } catch (error) {
          setEnabledOverrides((current) => ({ ...current, [restoring.id]: false }))
          throw error
        }
      }
      toast.success(`${restoring.name} is active`)
      setRestoreOpen(false)
    } catch (error) {
      setRestoreError(extractErrorMessage(error, 'The intake could not be made active.'))
    } finally {
      setRestoreBusy(false)
    }
  }

  const toggleEnabled = async (intake: IngestIntake, enabled: boolean) => {
    setEnabledOverrides((current) => ({ ...current, [intake.id]: enabled }))
    try {
      await patchIntake({ id: intake.id, body: { enabled } }).unwrap()
    } catch {
      setEnabledOverrides((current) => ({ ...current, [intake.id]: intake.enabled }))
      toast.error(`Could not ${enabled ? 'enable' : 'disable'} ${intake.name}`)
    }
  }

  const copyCurl = async (intake: IngestIntake) => {
    try {
      const curl = buildIntakeUploadCurl({
        proxyApiV1Url: PROXY_API_V1_URL,
        intakeName: intake.name,
        token: API_KEY_TOKEN_PLACEHOLDER
      })
      await writeClipboardPayload({ text: curl })
      setCopiedId(intake.id)
      toast.success('Upload command copied')
      window.setTimeout(
        () => setCopiedId((current) => (current === intake.id ? null : current)),
        1500
      )
    } catch {
      toast.error('Could not copy upload command')
    }
  }

  const restoreIds = restoring ? intakeUnarchiveVersionIds(restoring, configs ?? []) : []
  const restoreConfigName =
    restoring?.targetKind === 'mapped_tabular_ingest'
      ? (restoring.target as IngestIntakeTabularTarget).configName
      : ''

  return (
    <>
      <Card className="gap-0 overflow-hidden py-0">
        <div className="overflow-x-auto">
          {/* table-fixed pins column widths to the <th> sizes so the expansion
            row's colSpan cell cannot reflow the layout when it opens. */}
          <table className="w-full table-fixed text-left text-sm">
            <thead className="border-b border-line text-ink-3">
              <tr>
                <th className="px-4 py-2 text-xs font-medium uppercase tracking-wider">Name</th>
                <th className="w-35 py-2 pr-3 text-xs font-medium uppercase tracking-wider">
                  Site
                </th>
                <th className="w-47.5 py-2 pr-3 text-xs font-medium uppercase tracking-wider">
                  Target
                </th>
                <th className="w-45 py-2 pr-3 text-xs font-medium uppercase tracking-wider">
                  Branch
                </th>
                <th className="w-24 py-2 pr-3 text-xs font-medium uppercase tracking-wider">
                  Received
                </th>
                <th className="w-18 py-2 pr-3 text-xs font-medium uppercase tracking-wider">
                  Enabled
                </th>
                <th className="w-13 py-2 pr-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {intakes.map((intake) => {
                const expanded = expandedId === intake.id
                const enabled = enabledOverrides[intake.id] ?? intake.enabled
                const target = targetCell(intake, siteNames, configs)
                const blockedVersionIds = intakeUnarchiveVersionIds(intake, configs ?? [])
                const attention = attentionPills(intake)
                const branch = intake.branch
                const siteLabel = siteNames[intake.siteId] ?? intake.siteId
                const toggle = () => onExpandedChange(expanded ? null : intake.id)
                return (
                  <React.Fragment key={intake.id}>
                    <tr
                      role="button"
                      tabIndex={0}
                      aria-expanded={expanded}
                      onClick={toggle}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          toggle()
                        }
                      }}
                      className={cn(
                        'cursor-pointer border-b border-line transition-colors hover:bg-bg-sunk focus-visible:bg-bg-sunk focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30',
                        expanded && 'bg-bg-sunk'
                      )}
                    >
                      <td className="overflow-hidden px-4 py-2">
                        <div
                          className={cn(
                            'truncate font-mono text-xs',
                            enabled ? 'text-ink' : 'text-ink-3'
                          )}
                          title={intake.name}
                        >
                          {intake.name}
                        </div>
                        {attention.length > 0 ? (
                          <div
                            className="mt-0.5 flex flex-wrap gap-1"
                            data-testid="intake-attention"
                          >
                            {attention.map((pill) => (
                              <StatusPill
                                key={pill.label}
                                status={pill.status}
                                title={pill.tooltip}
                                className="shrink-0"
                              >
                                {pill.label}
                              </StatusPill>
                            ))}
                          </div>
                        ) : null}
                      </td>
                      <td
                        className="overflow-hidden py-2 pr-3 text-xs text-ink-2"
                        title={siteLabel}
                      >
                        <div className="truncate">{siteLabel}</div>
                      </td>
                      <td className="overflow-hidden py-2 pr-3 text-xs">
                        <TargetCell intakeId={intake.id} target={target} />
                      </td>
                      <td className="overflow-hidden py-2 pr-3">
                        <div className="flex min-w-0 flex-col">
                          <span
                            className="truncate font-mono text-11 text-ink-2"
                            title={intake.branch?.name ?? intake.branchName}
                          >
                            {intake.branch?.name ?? intake.branchName}
                          </span>
                          <span className="text-11 text-ink-3">
                            {intake.branch ? 'unmerged changes' : 'nothing unmerged'}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-xs text-ink-3">
                        {formatRelativeTime(intake.lastReceivedAt, { fallback: '—' })}
                      </td>
                      <td className="py-2 pr-3">
                        <Switch
                          aria-label={`${enabled ? 'Disable' : 'Enable'} ${intake.name}`}
                          checked={enabled}
                          onCheckedChange={(next) => void toggleEnabled(intake, next)}
                          onClick={(event) => event.stopPropagation()}
                        />
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <div
                          className="inline-flex"
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                size="icon-sm"
                                variant="ghost"
                                aria-label={`Actions for ${intake.name}`}
                              >
                                <MoreHorizontal className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onSelect={() => onEdit(intake)}>
                                <Pencil />
                                Edit
                              </DropdownMenuItem>
                              {blockedVersionIds.length > 0 ? (
                                <DropdownMenuItem onSelect={() => openRestore(intake)}>
                                  <RotateCcw />
                                  Make active…
                                </DropdownMenuItem>
                              ) : null}
                              <DropdownMenuItem onSelect={() => void copyCurl(intake)}>
                                <Copy />
                                Copy upload command
                              </DropdownMenuItem>
                              {branch ? (
                                <>
                                  <DropdownMenuItem
                                    disabled={isOpening}
                                    onSelect={() => void switchBranch(branch.id)}
                                  >
                                    <GitBranch />
                                    Switch to branch
                                  </DropdownMenuItem>
                                  <DropdownMenuItem asChild>
                                    <Link to={intakeMergeReviewPath(branch.id, intake.id)}>
                                      <ExternalLink />
                                      Review &amp; merge
                                    </Link>
                                  </DropdownMenuItem>
                                </>
                              ) : null}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                variant="destructive"
                                onSelect={() => onDelete(intake)}
                              >
                                <Trash2 />
                                Delete…
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                    <ExpansionRow
                      intake={intake}
                      target={target}
                      isOpen={expanded}
                      copied={copiedId === intake.id}
                      switchingBranch={isOpening}
                      onCopyCurl={() => void copyCurl(intake)}
                      onSwitchBranch={(branchId) => void switchBranch(branchId)}
                      {...(blockedVersionIds.length > 0
                        ? { onMakeActive: () => openRestore(intake) }
                        : {})}
                    />
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="border-t border-line px-4 py-2 text-xs text-ink-3">
          {intakes.length} intake{intakes.length === 1 ? '' : 's'}. Open one for its push URL,
          accepted types, branch, and received files.
        </div>
      </Card>
      <UnarchiveConfigDialog
        open={restoreOpen}
        onOpenChange={(open) => {
          if (!open && !restoreBusy) setRestoreOpen(false)
        }}
        name={restoreConfigName}
        versionCount={restoreIds.length}
        loading={restoreBusy}
        error={restoreError ?? undefined}
        intake={
          restoring
            ? {
                name: restoring.name,
                enabled: enabledOverrides[restoring.id] ?? restoring.enabled
              }
            : undefined
        }
        onConfirm={() => void confirmRestore()}
      />
    </>
  )
}

function TargetCell({ intakeId, target }: { intakeId: string; target: IntakeTargetCell }) {
  if (target.kind === 'none') {
    return <StatusPill status="warn">no target</StatusPill>
  }
  const title = [
    `${target.label} · ${target.detail ?? ''}`.trim(),
    target.archivedNote,
    target.refusedNote
  ]
    .filter(Boolean)
    .join('\n')
  if (target.kind === 'tabular' && target.configName) {
    return (
      <Link
        to={tabularConfigPath(target.configName)}
        state={{ from: intakesTabPath(intakeId) }}
        onClick={(event) => event.stopPropagation()}
        title={title}
        className="flex w-full min-w-0 flex-col items-start text-st-info hover:underline"
      >
        <span className="inline-flex max-w-full items-center gap-1">
          <span className="truncate">{target.label}</span>
          <ArrowUpRight className="size-3 shrink-0" aria-hidden />
          {target.archivedNote ? (
            <StatusPill status="down" className="shrink-0 no-underline">
              archived
            </StatusPill>
          ) : null}
        </span>
        <span className="block max-w-full truncate text-11 font-normal text-ink-3 no-underline">
          {target.detail}
        </span>
        {target.archivedNote ? (
          <span className="block max-w-full truncate text-11 font-normal text-st-down no-underline">
            new files fail
          </span>
        ) : null}
        {target.refusedExtensions ? (
          <span
            className="block max-w-full truncate text-11 font-normal text-st-warn no-underline"
            data-testid="refused-types"
          >
            refuses {target.refusedExtensions.join(', ')}
          </span>
        ) : null}
      </Link>
    )
  }
  return (
    <div className="flex min-w-0 flex-col text-ink" title={title}>
      <span className="truncate">{target.label}</span>
      <span className="truncate text-11 text-ink-3">{target.detail}</span>
    </div>
  )
}

interface ExpansionRowProps {
  intake: IngestIntake
  target: IntakeTargetCell
  isOpen: boolean
  copied: boolean
  switchingBranch: boolean
  onCopyCurl: () => void
  onSwitchBranch: (branchId: string) => void
  onMakeActive?: () => void
}

function ExpansionRow({
  intake,
  target,
  isOpen,
  copied,
  switchingBranch,
  onCopyCurl,
  onSwitchBranch,
  onMakeActive
}: ExpansionRowProps) {
  // Stay mounted through the close animation so the collapse is smooth.
  const [isMounted, setIsMounted] = React.useState(isOpen)
  React.useEffect(() => {
    if (isOpen) {
      setIsMounted(true)
      return undefined
    }
    const timer = setTimeout(() => setIsMounted(false), EXPAND_MS)
    return () => clearTimeout(timer)
  }, [isOpen])

  const url = intakePushFilesUrl(PROXY_API_V1_URL, intake.name)
  const branch = intake.branch

  return (
    <tr className={isOpen ? 'border-b border-line' : ''}>
      <td colSpan={COLUMN_COUNT} className="p-0">
        <div
          data-open={isOpen}
          className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none data-[open=true]:grid-rows-[1fr]"
        >
          <div className="min-h-0 overflow-hidden">
            <div
              data-open={isOpen}
              className="bg-bg-sunk px-4 py-3 opacity-0 transition-opacity duration-200 ease-out motion-reduce:transition-none data-[open=true]:opacity-100"
            >
              {isMounted ? (
                <div className="flex flex-col gap-3">
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1.5 text-11">
                    <dt className="pt-px text-10 font-medium tracking-[0.06em] text-ink-3 uppercase">
                      Push URL
                    </dt>
                    <dd className="flex min-w-0 items-center gap-1.5">
                      <span className="min-w-0 truncate font-mono text-ink" title={url}>
                        {url}
                      </span>
                      <button
                        type="button"
                        aria-label={`Copy upload command for ${intake.name}`}
                        title="Copy a curl command for this intake"
                        onClick={onCopyCurl}
                        className="inline-flex size-5 shrink-0 items-center justify-center rounded text-ink-3 transition-colors hover:bg-bg-elev hover:text-ink"
                      >
                        {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
                      </button>
                    </dd>

                    <dt className="pt-px text-10 font-medium tracking-[0.06em] text-ink-3 uppercase">
                      Accepts
                    </dt>
                    <dd className="flex flex-wrap gap-1">
                      {intake.accept.map((extension) => (
                        <span
                          key={extension}
                          className="rounded border border-line bg-bg-elev px-1.5 py-px font-mono text-10 text-ink-2"
                        >
                          {extension.replace(/^\./, '')}
                        </span>
                      ))}
                    </dd>

                    <dt className="pt-px text-10 font-medium tracking-[0.06em] text-ink-3 uppercase">
                      Branch
                    </dt>
                    <dd className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-mono text-ink">
                        {branch?.name ?? intake.branchName}
                      </span>
                      {branch ? (
                        <>
                          <StatusPill status="run">active</StatusPill>
                          <button
                            type="button"
                            disabled={switchingBranch}
                            onClick={() => onSwitchBranch(branch.id)}
                            className="text-st-info hover:underline disabled:opacity-50"
                          >
                            Switch to branch
                          </button>
                          <Link
                            to={intakeMergeReviewPath(branch.id, intake.id)}
                            className="inline-flex items-center gap-1 text-st-info hover:underline"
                          >
                            Review &amp; merge
                            <ExternalLink className="size-3" aria-hidden />
                          </Link>
                        </>
                      ) : (
                        <span className="text-ink-3">
                          Reserved, nothing unmerged. The next file creates a fresh branch from
                          main.
                        </span>
                      )}
                    </dd>

                    <dt className="pt-px text-10 font-medium tracking-[0.06em] text-ink-3 uppercase">
                      Target
                    </dt>
                    <dd className="min-w-0">
                      {target.kind === 'none' ? (
                        <span className="text-ink-3">
                          None. Files park as pending until a target is set.
                        </span>
                      ) : target.kind === 'tabular' && target.configName ? (
                        <>
                          <Link
                            to={tabularConfigPath(target.configName)}
                            state={{ from: intakesTabPath(intake.id) }}
                            className="inline-flex items-center gap-1 text-st-info hover:underline"
                          >
                            {target.label} · {target.detail}
                            <ArrowUpRight className="size-3" aria-hidden />
                          </Link>
                          {target.archivedNote ? (
                            <div className="mt-0.5 flex flex-col items-start gap-1.5">
                              <p className="text-st-down" data-testid="archived-target-note">
                                {target.archivedNote}
                              </p>
                              {onMakeActive ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2.5 text-xs"
                                  onClick={onMakeActive}
                                >
                                  Make active…
                                </Button>
                              ) : null}
                            </div>
                          ) : null}
                          {target.refusedNote ? (
                            <p className="mt-0.5 text-st-warn" data-testid="refused-types-note">
                              {target.refusedNote}
                            </p>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-ink">
                          {target.label} · {target.detail}
                        </span>
                      )}
                    </dd>

                    {intake.description ? (
                      <>
                        <dt className="pt-px text-10 font-medium tracking-[0.06em] text-ink-3 uppercase">
                          Description
                        </dt>
                        <dd className="text-ink-2">{intake.description}</dd>
                      </>
                    ) : null}
                  </dl>

                  <IntakeLedger intake={intake} />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </td>
    </tr>
  )
}
